import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { FilterItem, GenericTableHeaderOptions, RefreshData } from '../../models/generic-table-header-options.model';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { formatDate } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { CustomDateAdapter, CUSTOM_DATE_FORMATS } from 'libs/portal/src/modules/tools/card-table/components/custom-date-adapter';

@Component({
  selector: 'app-generic-table-header',
  templateUrl: './generic-table-header.component.html',
  styleUrls: ['./generic-table-header.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
  ],
})
export class GenericTableHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() config: GenericTableHeaderOptions;
  @Input() savedSearch: string;

  @Output() newItemAdded = new EventEmitter<string>();
  @Output() refreshEvent = new EventEmitter<RefreshData>();
  @Output() exportPressed = new EventEmitter<RefreshData>();
  @Output() importEvent = new EventEmitter<void>();
  @Output() selectAllPressed = new EventEmitter<boolean>();
  @Output() dateRangeChange = new EventEmitter<any>();
  @Output() dropdownSelected = new EventEmitter<any>();
  @Output() viewPressed = new EventEmitter<any>();
  @Output() clearPressed = new EventEmitter<any>();
  @Output() startDateTimeChange = new EventEmitter<any>();
  @Output() endDateTimeChange = new EventEmitter<any>();
  @Output() filterValueChanged = new EventEmitter<any>();
  searchQuery = new UntypedFormControl('');

  sortValue: string;
  sortOrderIsDesc = true;

  startDateForm: UntypedFormControl = new UntypedFormControl();
  endDateForm: UntypedFormControl = new UntypedFormControl();

  sortChange = false;
  showFilters = false;

  enableDateSearch = false;
  selectAll = false;

  formSubs: Subscription[] = [];
  filterForms: Map<string, UntypedFormControl> = new Map<string, UntypedFormControl>();

  searchFiltersLength = 0;
  selectionFiltersLength = 0;

  constructor(private tools: ToolsService, private toastService: ToastrService) {}

  ngOnInit(): void {
    this.searchFiltersLength = this.getSearchFilters().length;
    this.selectionFiltersLength = this.getSelectionFilters().length;
    if (this.config?.FilterItems && this.config?.FilterItems.length > 0) {
      this.config?.FilterItems.forEach((elem) => {
        this.filterForms.set(elem.Name, new UntypedFormControl(elem.DefaultValue));
      });
    }
    if (this.config?.ToggleFilters && this.config?.ToggleFilters.length > 0) {
      this.config?.ToggleFilters.forEach((elem) => {
        this.filterForms.set(elem.Name, new UntypedFormControl(elem.DefaultValue ? elem.DefaultValue : false));
      });
    }
    if (this.config?.DateItem) {
      this.enableDateSearch = true;
    }
    if (this.config?.DateItem && this.config?.DateItem.StartDate) {
      const formattedStartDate = this.formatDate(this.config.DateItem.StartDate);
      this.startDateForm.setValue(formattedStartDate);
      if (this.config.DateItem.Singular) {
        // Use extractYearMonth to set the value as `yyyy/MM`
        this.startDateForm.setValue(this.extractYearMonth(formattedStartDate)); 
      } else {
        // Set full date if not singular
        this.startDateForm.setValue(formattedStartDate);
      }
    }
    if (this.config?.DateItem && !this.config?.DateItem.Singular && this.config?.DateItem.EndDate) {
      const formattedEndDate = this.formatDate(this.config.DateItem.EndDate);
      this.endDateForm.setValue(formattedEndDate);
    }
    if (this.savedSearch != null) {
      this.searchQuery.setValue(this.savedSearch);
    }
    if (this.config?.DefaultSort) {
      this.sortValue = this.config?.DefaultSort;
    }
    if (this.config?.DefaultDesc != null) {
      this.sortOrderIsDesc = this.config?.DefaultDesc;
    }

    this.filterForms.forEach((value: any, key: string) => {
      let sub = value.valueChanges.pipe(debounceTime(150)).subscribe((val) => {
        this.filterValueChanged.emit({
          Name: key,
          value: val,
        });
      });
      this.formSubs.push(sub);
    });
  }

  ngOnDestroy(): void {
      this.formSubs.forEach(s => s.unsubscribe());
  }

  ngOnChanges(): void {
    this.ngOnInit();
    this.searchQuery.setValue('');
    this.sortChange = false;
    this.showFilters = false;
    this.selectAll = false;
  }

  formatDate(date: Date | string): string {
    const d = new Date(date);
    return formatDate(d, 'yyyy-MM-dd', 'en-US'); // Customize format as needed
  }

  extractYearMonth(dateString: string): string {
    const dateParts = dateString.split('-'); // Split the string by the hyphen
  const year = dateParts[0];               // First part is the year
  const month = dateParts[1];              // Second part is the month
  
  return `${year}/${month}`;               // Return in YYYY/MM format
  }

  clearSearch(): void {
    this.searchQuery.setValue('');
    this.submitRefresh();
  }

  selectAllPress(): void {
    this.selectAll = !this.selectAll;
    this.selectAllPressed.emit(this.selectAll);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  toggleDateSearch(): void {
    this.enableDateSearch = !this.enableDateSearch;
  }

  submitRefresh(): void {
    const refreshData: RefreshData = {
      Search: this.searchQuery.value == '' ? null : this.searchQuery.value,
      Sort: this.sortValue,
      Desc: this.sortOrderIsDesc,
      Filters: Array.from(this.filterForms)
        .map((elem) => {
          if (elem[1].valid == null) {
            return null;
          }
          return {
            Name: elem[0],
            value: elem[1].value,
          };
        })
        .filter((elem) => elem != null),
      FromDate: this.enableDateSearch && this.startDateForm.value ? this.tools.FormatStartDate(this.startDateForm.value) : null,
      ToDate: !this.enableDateSearch || this.config?.DateItem?.Singular || !this.endDateForm.value ? null : this.tools.FormatEndDate(this.endDateForm.value),
    };
    this.refreshEvent.emit(refreshData);
  }

  applyFilters(): void {
    this.showFilters = !this.showFilters;
    this.submitRefresh();
  }

  exportPress(): void {
    if (this.config?.DateRequired && (!this.startDateForm.valid || this.startDateForm.value == null || !this.enableDateSearch)) {
      this.toastService.info('', 'Enter a valid date.', { timeOut: 2000 });
      return;
    }
    const refreshData: RefreshData = {
      Search: this.searchQuery.value == '' ? null : this.searchQuery.value,
      Sort: this.sortValue,
      Desc: this.sortOrderIsDesc,
      Filters: Array.from(this.filterForms)
        .map((elem) => {
          if (elem[1].valid == null) {
            return null;
          }
          return {
            Name: elem[0],
            value: elem[1].value,
          };
        })
        .filter((elem) => elem != null),
      FromDate: this.startDateForm?.value ? this.tools.FormatStartDate(this.startDateForm.value) : null,
      ToDate: this.config?.DateItem?.Singular || !this.endDateForm.value ? null : this.tools.FormatEndDate(this.endDateForm.value),
    };
    this.exportPressed.emit(refreshData);
  }

  importPressed(): void {
    this.importEvent.emit();
  }

  sortChanged(): void {
    this.sortChange = true;
  }

  sortDirectionChanged(): void {
    this.sortOrderIsDesc = !this.sortOrderIsDesc;
    this.sortChange = true;
  }
  sortApplied(): void {
    if (!this.sortChange) {
      //clear
      this.sortValue = null;
      this.sortOrderIsDesc = true;
    }
    this.sortChange = false;
    this.submitRefresh();
  }

  datePickerChanged(date: Date): void {
    this.startDateForm.setValue(date);
  }

  clearFilters(): void {
    this.filterForms.forEach((val) => val.setValue(null));
    this.showFilters = !this.showFilters;
    this.submitRefresh();
  }

  getSearchFilters(): FilterItem[] {
    if (!this.config?.FilterItems) {
      return [];
    }
    return this.config?.FilterItems.filter((elem) => elem.Values == null || elem.Values.length == 0);
  }

  getSelectionFilters(): FilterItem[] {
    if (!this.config?.FilterItems) {
      return [];
    }
    return this.config?.FilterItems.filter((elem) => elem.Values != null && elem.Values.length > 0);
  }

  dateChangeOccurred(event: any) {
    this.dateRangeChange.emit({
      startDate: this.startDateForm.value,
      endDate: this.endDateForm.value,
    });
  }

  dropdownEmit(event: any, item: any) {
    console.log(event, item);
    this.dropdownSelected.emit({
      Label: item.Label,
      Value: event.value,
    });
  }
}
